import React, { useState, useEffect, useRef } from "react";
import styles from "./Toast.module.css";

// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  selectAppOpen,
  selectMenuOpen,
} from "../../redux/features/applicationState/applicationStateSlice";
import { setToastVisibilityStatus } from "../../redux/features/applicationState/applicationStateSlice";

// Icons
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const Toast = (props) => {
  const dispatch = useDispatch();
  const { toastType, title, message, toastDuration } = props;

  const initialRenderWait = 100;
  const animationDuration = 300;
  const toastDurationTime = toastDuration ? toastDuration : 5000;

  const displayTimerRef = useRef(null);
  const autoHideTimerRef = useRef(null);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  const appOpenStatus = useSelector(selectAppOpen);
  const menuOpenStatus = useSelector(selectMenuOpen);

  // Types of toast
  const typeColors = {
    success: "bg-green-200",
    error: "bg-red-200",
    info: "bg-cyan-100",
    warning: "bg-yellow-100",
  };

  const toastIcons = {
    success: CheckCircleIcon,
    error: ExclamationCircleIcon,
    info: InformationCircleIcon,
    warning: ExclamationTriangleIcon,
  };

  let ToastIcon = toastIcons[toastType];

  const closeToastHandler = () => {
    clearTimeout(autoHideTimerRef.current); // Clear the auto-hide timer

    setIsAnimatingOut(true);

    setTimeout(() => {
      dispatch(setToastVisibilityStatus(false));
    }, animationDuration);
  };

  const [timer, setTimer] = useState(null);

  useEffect(() => {
    // Clear existing timers
    clearTimeout(displayTimerRef.current);
    clearTimeout(autoHideTimerRef.current);

    // Wait 100ms for the toast component to mount and then setToastOpen(true)
    displayTimerRef.current = setTimeout(() => {
      setIsAnimatingOut(false);

      // Start a new timer to hide the toast after 5 seconds
      autoHideTimerRef.current = setTimeout(() => {
        setIsAnimatingOut(true);
        setTimeout(() => {
          dispatch(setToastVisibilityStatus(false));
        }, animationDuration);
      }, toastDurationTime);
    }, initialRenderWait); // delay before showing the toast

    // Cleanup function to clear timers when component is unmounted or when dependencies change
    return () => {
      clearTimeout(displayTimerRef.current);
      clearTimeout(autoHideTimerRef.current);
    };
  }, [toastType, title, message, toastDuration]);

  return (
    // Toast container
    <div
      className={`fixed z-50 top-0 left-0 flex flex-row items-center justify-center w-full md:pt-0 pointer-events-none transition-all duration-200 Xbg-red-500
                  ${
                    !appOpenStatus
                      ? "pl-0"
                      : menuOpenStatus
                      ? "pt-10 md:pl-[6.5rem] ip11l:pl-[25.5rem]"
                      : "pt-10 md:pl-[6.5rem]"
                  }
                 `}
    >
      {/* Toast */}
      <div
        className={`flex flex-row items-center justify-center w-full h-full md:px-2 Xbg-yellow-500 ${
          !appOpenStatus ? "px-2" : "px-4"
        }`}
      >
        <div
          className={`flex items-center justify-between min-h-[200px] p-5 pointer-events-auto rounded-xl shadow-2xl bg-opacity-95
                flex-col sm:flex-col md:flex-row
                w-full sm:w-full lg:w-10/12 xl:w-8/12
                mt-5 sm:mt-5 md:mt-10
                ${typeColors[toastType]}
                ${
                  isAnimatingOut
                    ? styles["toast-slide-up"]
                    : styles["toast-slide-down"]
                }
               `}
        >
          {/* Icon */}
          <div
            className="flex flex-[2] items-center justify-center bg-blue-5
          pb-5 sm:pb-5 lg:pb-0
          "
          >
            <ToastIcon
              className="text-neutral-500
                   h-40 sm:h-40 md:h-20
                   w-40 sm:w-40 md:w-20
                  "
            />
          </div>
          {/* Content container */}
          <div className="flex flex-col flex-[7] w-full bg-green-5">
            {/* Title */}
            <p
              className="font-medium pb-10 text-neutral-500
                   text-2xl sm:text-2xl md:text-3xl lg:text-4xl
                  "
            >
              {title}
              {/* {toastTitle} */}
            </p>
            {/* Message */}
            <p className=" text-lg font-normal text-neutral-500">
              {message}
              {/* {toastMessage} */}
            </p>
          </div>
          {/* Close Icon */}
          <div className="flex flex-[1] items-center justify-center bg-red-5 w-full">
            <button
              className="flex items-center justify-center p-1 rounded-md
                   bg-neutral-500 hover:bg-neutral-600 active:bg-neutral-700
                   w-full sm:w-full md:w-auto
                   mt-5 sm:mt-5 md:mt-0
                  "
              onClick={() => {
                closeToastHandler();
              }}
            >
              <XMarkIcon className="w-10 h-10 text-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Toast;
